import type { Translate } from '@client-app/i18n';

export const PASSWORD_MIN_LENGTH = 8;

// Preserve the exact value sent to the provider, including whitespace and Unicode.
export function getNewPasswordError(password: string, t: Translate): string | null {
    if (!password) return t('website.auth.validation.enterNewPassword');
    if (Array.from(password).length < PASSWORD_MIN_LENGTH) {
        return t('website.auth.validation.passwordMin');
    }
    return null;
}

// Provider rejection is separate from our form policy. Never infer password history
// from a weak-password response or an unrelated HTTP 422 validation error.
export function getPasswordUpdateError(error: unknown, t: Translate): string | null {
    if (!error || typeof error !== 'object') return null;
    const providerError = error as {
        code?: string;
        message?: string;
        reasons?: string[];
        weak_password?: { reasons?: string[] };
    };
    const message = String(providerError.message || '').toLowerCase();
    if (providerError.code === 'same_password'
        || message.includes('new password should be different from the old password')
        || message.includes('new password must be different from the current password')
        || message.includes('new password is the same as the old password')) {
        return t('website.auth.errors.passwordReused');
    }

    const isWeakPassword = providerError.code === 'weak_password'
        || message.includes('weak password')
        || message.includes('password is too weak')
        || message.includes('password should be at least');
    if (!isWeakPassword) return null;

    const reasons = providerError.reasons || providerError.weak_password?.reasons || [];
    if (reasons.includes('pwned') || reasons.includes('compromised')) {
        return t('website.auth.errors.passwordCompromised');
    }
    const minimumLength = message.match(/password should be at least (\d+) characters/);
    if (minimumLength) {
        return t('website.auth.validation.providerPasswordMin', { length: Number(minimumLength[1]) });
    }
    return t('website.auth.errors.passwordTooWeak');
}
